import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import AuthShell from "./AuthShell";
import { useAuth } from "@/lib/auth";
import { BRAND } from "@/branding.config";

/**
 * Admin portal login — accepts both `admin` and `super_admin` accounts.
 * Rejects clients server-side; a friendly error is shown here.
 */
export default function AdminLogin() {
  const navigate = useNavigate();
  const { adminLogin } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    const res = await adminLogin(email, password);
    setBusy(false);
    if (res.ok) {
      toast.success("Welcome back");
      // If they must change password, PortalLayout / router will block them
      // and the ForcePasswordChange modal will show automatically.
      navigate("/admin");
    } else {
      setError(res.error);
    }
  };

  return (
    <AuthShell
      title="Admin sign in"
      subtitle={`Restricted portal for ${BRAND.app_name} operators.`}
      footer={null}
    >
      <form onSubmit={onSubmit} className="space-y-4" data-testid="admin-login-form">
        <div className="flex items-center gap-2 rounded-xl border border-primary/25 bg-primary/8 px-3 py-2 text-[11.5px] text-muted-foreground">
          <ShieldCheck size={14} className="text-primary shrink-0" />
          Admin credentials only. Clients use the customer portal.
        </div>

        <div className="space-y-1.5">
          <label className="text-xs font-medium text-muted-foreground">Email</label>
          <input
            type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
            data-testid="admin-login-email"
            placeholder="you@company.com"
            className="w-full rounded-xl bg-muted/40 border border-border px-4 py-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
          />
        </div>
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-muted-foreground">Password</label>
          <input
            type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
            data-testid="admin-login-password"
            placeholder="••••••••"
            className="w-full rounded-xl bg-muted/40 border border-border px-4 py-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
          />
        </div>

        {error && (
          <div data-testid="admin-login-error" className="text-sm text-red-500 bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2">
            {error}
          </div>
        )}

        <button
          type="submit" disabled={busy} data-testid="admin-login-submit"
          className="btn-primary w-full py-3 text-sm"
        >
          {busy ? "Signing in…" : "Sign in to admin"}
        </button>
      </form>
    </AuthShell>
  );
}
